import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getClickAnalytics,
  getInefficiencies,
  getAnalyticsSummary,
} from "../../store/thunks/analyticsThunk";
import { toast } from "react-toastify";

const ClickAnalyticsDashboard = () => {
  const dispatch = useDispatch();
  const { analytics, inefficiencies, summary, loading, error } = useSelector(
    (state) => state.analytics
  );

  const [daysBack, setDaysBack] = useState(7);
  const [selectedPath, setSelectedPath] = useState("");
  const [selectedRole, setSelectedRole] = useState("");

  // Load initial data
  useEffect(() => {
    loadAnalytics();
  }, [daysBack]);

  const loadAnalytics = () => {
    const startDate = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString();

    const filters = { startDate };
    if (selectedPath) filters.path = selectedPath;
    if (selectedRole) filters.userRole = selectedRole;

    dispatch(getClickAnalytics(filters));
    dispatch(getInefficiencies(daysBack));
    dispatch(getAnalyticsSummary(daysBack));
  };

  const handleRefresh = () => {
    loadAnalytics();
    toast.info("Analytics refreshed");
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "CRITICAL":
        return "text-red-600 bg-red-100";
      case "HIGH":
        return "text-orange-600 bg-orange-100";
      case "MEDIUM":
        return "text-yellow-600 bg-yellow-100";
      case "LOW":
        return "text-blue-600 bg-blue-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Click Analytics & UX Insights
        </h1>
        <p className="text-gray-600">
          Analyze user interactions and identify workflow inefficiencies
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Days Back Selector */}
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Period
            </label>
            <select
              value={daysBack}
              onChange={(e) => setDaysBack(parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          {/* Path Filter */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Page Path
            </label>
            <input
              type="text"
              value={selectedPath}
              onChange={(e) => setSelectedPath(e.target.value)}
              placeholder="/clients, /schedule, etc."
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          {/* Role Filter */}
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Role
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">All Roles</option>
              <option value="owner">Owner</option>
              <option value="office_manager">Office Manager</option>
              <option value="clinician">Clinician</option>
              <option value="front_desk">Front Desk</option>
            </select>
          </div>

          {/* Apply Button */}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? "Loading..." : "Apply Filters"}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total Clicks</div>
            <div className="text-3xl font-bold text-gray-900">
              {summary.totalClicks?.toLocaleString() || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Unique Users</div>
            <div className="text-3xl font-bold text-gray-900">
              {summary.uniqueUsers || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Top Page</div>
            <div className="text-lg font-bold text-gray-900 truncate">
              {summary.topPages?.[0]?._id || "N/A"}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Inefficiencies Found</div>
            <div className="text-3xl font-bold text-orange-600">
              {inefficiencies?.length || 0}
            </div>
          </div>
        </div>
      )}

      {/* Inefficiency Alerts */}
      {inefficiencies && inefficiencies.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            🚨 Workflow Inefficiencies Detected
          </h2>
          <div className="space-y-3">
            {inefficiencies.slice(0, 10).map((item, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 mb-1">
                      {item._id}
                    </div>
                    <div className="text-sm text-gray-600">
                      {item.recommendation}
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(
                      item.severity
                    )}`}
                  >
                    {item.severity}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                  <div>
                    <span className="text-gray-600">Avg Clicks: </span>
                    <span className="font-semibold">
                      {item.avgClicksPerSession.toFixed(1)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Max Clicks: </span>
                    <span className="font-semibold">{item.maxClicks}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Sessions: </span>
                    <span className="font-semibold">{item.sessionsCount}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Most Clicked Components */}
      {analytics?.analysis && analytics.analysis.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Most Clicked Components
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                    Component
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                    Element
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                    Page
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">
                    Clicks
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">
                    Users
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">
                    Avg Time (s)
                  </th>
                </tr>
              </thead>
              <tbody>
                {analytics.analysis.slice(0, 20).map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">
                      {item._id.componentName || "N/A"}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {item._id.elementText || item._id.elementType}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {item._id.path}
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900">
                      {item.clickCount}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">
                      {item.uniqueUserCount}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">
                      {item.avgTimeOnPage
                        ? (item.avgTimeOnPage / 1000).toFixed(1)
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Journeys */}
      {analytics?.journeys && analytics.journeys.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Common User Journeys
          </h2>
          <div className="space-y-4">
            {analytics.journeys.slice(0, 5).map((journey, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-2">
                  {journey.totalClicks} clicks
                </div>
                <div className="flex flex-wrap gap-2">
                  {journey.clickPath.map((step, stepIdx) => (
                    <React.Fragment key={stepIdx}>
                      <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-sm">
                        {step.component || step.path}
                      </div>
                      {stepIdx < journey.clickPath.length - 1 && (
                        <div className="text-gray-400">→</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <div className="text-gray-700">Loading analytics...</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClickAnalyticsDashboard;
